/*
  TRWS product page buying behavior (2026-11-03). Default product template only.
  Content features (Fit Guru card, spec table, APMA fix) live in trws-pdp-content.js and
  also run on the store-locator and presale templates.

  1. Size required: when a shopper lands without a ?variant= link, no size is pre-selected.
     Add to Cart reads "Select a size" and store availability waits until they pick one.
     Links that carry ?variant= (ads, Google Shopping, emails) keep their size.
     Only the native `disabled` attribute is used. Dawn's product form refuses to submit while
     aria-disabled="true" and never clears it, so this script never sets it.
  2. Sticky add-to-cart bar on every screen size once the main button scrolls out of view.
*/
(() => {
  if (window.__trwsPdpLoaded) return;
  window.__trwsPdpLoaded = true;

  const root = document.querySelector('product-info[id^="MainProduct"]');
  if (!root) return;

  const state = {
    sizeChosen: new URLSearchParams(window.location.search).has('variant'),
  };

  const qs = (selector) => root.querySelector(selector);
  const mainButton = () => qs('[id^="ProductSubmitButton"]');

  // Read only the legend's own text, not the hint this script appends to it.
  const legendText = (fs) =>
    Array.from(fs.querySelector('legend')?.childNodes || [])
      .filter((node) => !(node.nodeType === Node.ELEMENT_NODE && node.classList.contains('trws-size-hint')))
      .map((node) => node.textContent)
      .join('')
      .trim();
  const fieldsetByLegend = (pattern) =>
    Array.from(root.querySelectorAll('variant-selects fieldset')).find((fs) => pattern.test(legendText(fs)));
  const sizeFieldset = () => fieldsetByLegend(/^size\b/i);
  const colorFieldset = () => fieldsetByLegend(/^colou?r\b/i);
  const checkedValue = (fs) => fs?.querySelector('input:checked')?.value || '';

  const sizeRequired = () => {
    const fs = sizeFieldset();
    return !!fs && fs.querySelectorAll('input[type="radio"]').length > 1;
  };
  const needsSize = () => sizeRequired() && !state.sizeChosen;

  /* ---------- 1. Size required ---------- */

  const setWaiting = (waiting) => {
    const fs = sizeFieldset();
    const button = mainButton();
    const pickup = qs('pickup-availability');
    const dynamicCheckout = qs('.shopify-payment-button');
    const bis = qs('.klaviyo-bis-trigger');

    if (fs) {
      fs.classList.toggle('trws-size-needed', waiting);
      let hint = fs.querySelector('.trws-size-hint');
      if (waiting && !hint) {
        hint = document.createElement('span');
        hint.className = 'trws-size-hint';
        hint.textContent = 'Select your size';
        fs.querySelector('legend')?.appendChild(hint);
      }
      if (!waiting && hint) hint.remove();
      if (waiting) fs.querySelectorAll('input[type="radio"]').forEach((input) => (input.checked = false));
    }

    pickup?.classList.toggle('trws-hidden', waiting);
    dynamicCheckout?.classList.toggle('trws-hidden', waiting);
    bis?.classList.toggle('trws-hidden', waiting);

    if (!button) return;
    button.removeAttribute('aria-disabled');
    if (waiting) {
      button.disabled = true;
      button.dataset.trwsWaiting = 'true';
      const label = button.querySelector('span');
      if (label) label.textContent = 'Select a size';
    } else {
      delete button.dataset.trwsWaiting;
    }
  };

  root.addEventListener(
    'change',
    (event) => {
      const fs = sizeFieldset();
      if (fs && fs.contains(event.target)) {
        state.sizeChosen = true;
        setWaiting(false);
      }
    },
    true
  );

  /* ---------- 2. Sticky add-to-cart bar ---------- */

  const sticky = document.querySelector('[data-trws-sticky]');
  let stickyShown = false;

  const priceText = () => {
    const price = qs('[id^="price-"] .price');
    if (!price) return '';
    const onSale = price.classList.contains('price--on-sale');
    const item = onSale ? price.querySelector('.price-item--sale') : price.querySelector('.price__regular .price-item--regular');
    return (item?.textContent || '').trim();
  };

  const updateSticky = () => {
    if (!sticky) return;
    const button = sticky.querySelector('[data-trws-sticky-button]');
    const meta = sticky.querySelector('[data-trws-sticky-meta]');
    const main = mainButton();

    const parts = [];
    const color = checkedValue(colorFieldset());
    if (color) parts.push(color.replace(/\s*\(\w+\)\s*$/, ''));
    if (sizeRequired()) parts.push(needsSize() ? 'No size selected' : `Size ${checkedValue(sizeFieldset())}`);
    const price = priceText();
    if (price) parts.push(price);
    meta.textContent = parts.join(' · ');

    if (needsSize()) {
      button.textContent = 'Select size';
      button.disabled = false;
    } else if (!main || main.disabled) {
      button.textContent = (main?.textContent || '').trim() || 'Sold out';
      button.disabled = true;
    } else {
      button.textContent = 'Add to cart';
      button.disabled = false;
    }
  };

  // Shopify's preview bar (theme previews only) sits at the bottom of the screen. Lift the
  // sticky bar above it so previews look like the live site.
  const previewBarOffset = () => {
    const bar =
      document.getElementById('PBarNextFrameWrapper') ||
      document.getElementById('PBarNextFrame') ||
      document.getElementById('preview-bar-iframe');
    if (!bar) return 0;
    const rect = bar.getBoundingClientRect();
    if (!rect.height || rect.top < window.innerHeight / 2) return 0;
    return Math.max(0, Math.round(window.innerHeight - rect.top));
  };

  const showSticky = (show) => {
    if (!sticky || show === stickyShown) return;
    stickyShown = show;
    if (show) document.body.style.setProperty('--trws-sticky-offset', `${previewBarOffset()}px`);
    sticky.hidden = !show;
    sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
    sticky.querySelector('[data-trws-sticky-button]').tabIndex = show ? 0 : -1;
    document.body.classList.toggle('trws-sticky-active', show);
    if (show) updateSticky();
  };

  // Measure the live buy buttons on every scroll frame. Dawn re-renders parts of the buy box on
  // variant change, so a stored element (or an IntersectionObserver on it) can go stale.
  let ticking = false;
  const checkSticky = () => {
    ticking = false;
    const target = qs('.product-form__buttons') || mainButton();
    if (!target) return showSticky(false);
    const rect = target.getBoundingClientRect();
    showSticky(rect.bottom < 0 || rect.top > window.innerHeight);
  };
  const requestCheck = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(checkSticky);
  };

  if (sticky) {
    document.body.appendChild(sticky);
    window.addEventListener('scroll', requestCheck, { passive: true });
    window.addEventListener('resize', requestCheck, { passive: true });
    requestCheck();

    sticky.querySelector('[data-trws-sticky-button]').addEventListener('click', () => {
      if (needsSize()) {
        const fs = sizeFieldset();
        fs?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        fs?.classList.remove('trws-size-flash');
        void fs?.offsetWidth;
        fs?.classList.add('trws-size-flash');
        return;
      }
      const main = mainButton();
      if (main && !main.disabled) main.click();
    });
  }

  /* ---------- Keep state through Dawn's variant re-renders ---------- */

  let syncQueued = false;
  const sync = () => {
    syncQueued = false;
    if (needsSize() && (checkedValue(sizeFieldset()) || !mainButton()?.dataset.trwsWaiting)) setWaiting(true);
    if (stickyShown) updateSticky();
  };
  const queueSync = () => {
    if (syncQueued) return;
    syncQueued = true;
    requestAnimationFrame(sync);
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, queueSync);
  }
  // Variant pickers and the buy box are re-rendered in place; catch any re-render that skips the event.
  const buyArea = qs('variant-selects')?.parentElement ? root : null;
  if (buyArea) new MutationObserver(queueSync).observe(root, { childList: true, subtree: true });

  // Initial state
  if (needsSize()) setWaiting(true);
  updateSticky();
})();
